import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useDispatch } from 'react-redux'
import PropTypes from 'prop-types'
import { debounce } from 'lodash'
import { useTranslation } from 'react-i18next'

import FeaturedTable from '../FeaturedTable/FeaturedTable'
import { getSurveyHistory } from '../../reducers/surveyReducer'
import { useSnackbar } from '../../helpers/hooks/useSnackbar'
import API from '../../api/api'

import { surveyColumns } from './helpers'
import SurveyFormModal from './SurveyFormModal'

const SurveyTable = ({
  isSiteLoading,
  searchValue,
  buildingId,
  showOnPage,
  pageNumber,
  data,
  tableTypeFilters,
  tableStatusFilter,
  setPaginationItemsCount,
  refreshTable,
  selectedBuilding,
  selectedSurveyId,
  maxTableHeight,
  setSurveyToDelete,
  buildings,
  userRole,
}) => {
  const { t } = useTranslation(['surveys'])
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const [prevSearchValue, setPrevSearchValue] = useState('')
  const [selectedDataDetails, selectDataDetails] = useState(null)
  const [showSurveyDetails, setShowSurveyDetails] = useState(false)
  const { displaySnackbar } = useSnackbar()

  const handlePreviewOfMessage = (surveyId) => {
    navigate(`/dashboard/survey/${surveyId}`)
  }

  const fetchHistory = async (
    skip,
    searchingQuery,
    buildingId,
    tableTypeFilters,
    tableStatusFilter,
    tableDateFilter,
  ) => {
    if (buildingId) {
      if (typeof searchingQuery === 'undefined' || searchingQuery === '') {
        try {
          const count = await dispatch(
            getSurveyHistory(
              skip,
              buildingId,
              '',
              showOnPage,
              tableTypeFilters,
              tableStatusFilter,
              tableDateFilter,
            ),
          )
          setPaginationItemsCount(count.count)
        } catch (err) {
          throw new Error(err)
        }
      } else if (searchingQuery !== '') {
        try {
          const count = await dispatch(
            getSurveyHistory(
              skip,
              buildingId,
              searchingQuery,
              showOnPage,
              tableTypeFilters,
              tableStatusFilter,
              tableDateFilter,
            ),
          )
          setPaginationItemsCount(count.count)
        } catch (err) {
          throw new Error(err)
        }
      } else {
        try {
          const count = await dispatch(
            getSurveyHistory(
              1,
              buildingId,
              '',
              showOnPage,
              tableTypeFilters,
              tableStatusFilter,
              tableDateFilter,
            ),
          )
          setPaginationItemsCount(count)
        } catch (err) {
          throw new Error(err)
        }
      }
    }
  }

  const debounceFn = debounce(fetchHistory, 1000)

  useEffect(() => {
    if (prevSearchValue !== searchValue) {
      window.addEventListener(
        'keydown',
        debounceFn(undefined, searchValue, buildingId, tableTypeFilters, tableStatusFilter),
      )
    }
    setPrevSearchValue(searchValue)
    return () => {
      window.removeEventListener('keydown', debounceFn)
    }
  }, [searchValue])

  useEffect(() => {
    if (buildingId) {
      fetchHistory(pageNumber, searchValue, buildingId, tableTypeFilters, tableStatusFilter)
    }
  }, [pageNumber])

  useEffect(() => {
    if (selectedSurveyId && buildingId) {
      API.getSurveyDetails(selectedSurveyId)
        .then((res) => {
          setShowSurveyDetails(true)
          selectDataDetails(res.data)
        })
        .catch((err) => console.error(err))
    } else {
      setShowSurveyDetails(false)
    }
  }, [selectedSurveyId, buildingId])

  const onStop = async (id) => {
    try {
      await API.stopSurvey(id)
      refreshTable()
      displaySnackbar(t('surveys:table.successStop'))
    } catch (err) {
      console.error(err)
      displaySnackbar(t('surveys:table.errorStop'), true)
    }
  }

  const onResume = async (id) => {
    try {
      await API.resumeSurvey(id)
      refreshTable()
      displaySnackbar(t('surveys:table.successResume'))
    } catch (err) {
      console.error(err)
      displaySnackbar(t('surveys:table.errorResume'), true)
    }
  }

  const onCopy = async (id) => {
    try {
      await API.copySurvey(id)
      refreshTable()
      displaySnackbar(t('surveys:table.successCopy'))
    } catch (err) {
      console.error(err)
      displaySnackbar(t('surveys:table.errorCopy'), true)
    }
  }

  const goToDetailspPage = (id) => navigate(`/dashboard/survey/${id}`)

  return (
    <div className="ctp-survey__main-table" style={maxTableHeight}>
      {selectedBuilding && selectedBuilding.timezone && (
        <FeaturedTable
          columns={surveyColumns({
            t,
            timezone: selectedBuilding.timezone,
            showDetails: goToDetailspPage,
            onEdit: handlePreviewOfMessage,
            onDelete: setSurveyToDelete,
            onStop,
            onResume,
            onCopy,
          })}
          data={data}
        />
      )}
      {showSurveyDetails && !isSiteLoading && selectedDataDetails ? (
        <SurveyFormModal
          selectedBuilding={selectedBuilding}
          buildings={buildings}
          handleCancel={() => {
            selectDataDetails(null)
            navigate('/dashboard/survey')
            refreshTable()
          }}
          currentSurvey={selectedDataDetails}
          userRole={userRole}
        />
      ) : null}
    </div>
  )
}

SurveyTable.propTypes = {
  isSiteLoading: PropTypes.bool,
  searchValue: PropTypes.string,
  buildingId: PropTypes.number,
  showOnPage: PropTypes.number,
  pageNumber: PropTypes.number,
  data: PropTypes.array,
  tableTypeFilters: PropTypes.array,
  tableStatusFilter: PropTypes.object,
  setPaginationItemsCount: PropTypes.func,
  refreshTable: PropTypes.func,
  selectedBuilding: PropTypes.object,
  selectedSurveyId: PropTypes.string,
  maxTableHeight: PropTypes.object,
  setSurveyToDelete: PropTypes.func,
  buildings: PropTypes.array,
  userRole: PropTypes.string,
}

export default SurveyTable
